/**
 * Tracks the browser's network connectivity using the `online`/`offline`
 * events plus `navigator.onLine`.
 *
 * This is independent of our API layer: it fires for *any* lost connection,
 * including media that loads directly from S3 via a `<video>`/`<img>` tag
 * (which never passes through `baseService`). When the OS reports the network
 * is down the browser emits `offline`, and we surface a global banner.
 *
 * Note: `navigator.onLine === true` only guarantees a network interface is up,
 * not that the internet is reachable. We treat it as a best-effort signal —
 * good enough to tell the user "you're offline" without false negatives, and
 * the API layer still reports per-request `network_error` on top of this.
 */
import { useEffect, useState } from 'react'

function readOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false
}

export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(readOnline)

  useEffect(() => {
    const goOnline = () => setOnline(true)
    const goOffline = () => setOnline(false)

    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    // Resync in case the state changed before listeners were attached.
    setOnline(readOnline())

    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  return online
}
